import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, MessageCircle, ArrowLeft, Send, Phone, Video, PenSquare, X } from 'lucide-react';
import { useAuth, isOnline } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useCall } from '@/contexts/CallContext';
import { useMessages } from '@/contexts/MessageContext';
import { messageService, searchService } from '@/services/api';
import { supabase } from '@/lib/supabase';
import { UserAvatar } from '@/components/ui/Avatar';
import { CallRecord } from '@/components/chat/CallRecord';
import { cn, formatRelativeDate } from '@/lib/utils';

export default function MessagesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { startCall } = useCall();
  const { unreadByConversation, markConversationRead, setActiveConversation } = useMessages();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [people, setPeople] = useState<any[]>([]);
  const [searchingPeople, setSearchingPeople] = useState(false);
  const [opening, setOpening] = useState(false);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const openingRef = useRef<Set<string>>(new Set());
  const handledWithRef = useRef<string | null>(null);
  const peopleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!user) return;
      const { data } = await messageService.getConversations();
      if (cancelled) return;
      setConversations(data || []);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingMessages(true);
    messageService.getMessages(selectedId).then(({ data }) => {
      if (cancelled) return;
      setMessages(data || []);
      setLoadingMessages(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Opening a thread clears its unread count; closing the page releases it.
  useEffect(() => {
    setActiveConversation(selectedId);
    if (selectedId) void markConversationRead(selectedId);
    return () => {
      setActiveConversation(null);
    };
  }, [markConversationRead, selectedId, setActiveConversation]);

  // Live delivery for the open thread.
  useEffect(() => {
    if (!selectedId) return;
    const channel = supabase
      .channel(`messages-${selectedId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${selectedId}`,
        },
        (payload: any) => {
          const incoming = payload.new;
          setMessages((prev) =>
            prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming],
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, selectedId]);

  const otherMember = (c: any) =>
    c.conversation_members?.find((m: any) => m.user_id !== user?.id)?.profiles;

  const lastMessage = (c: any) => {
    const list = c.messages || [];
    return [...list].sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
  };

  const filtered = conversations.filter((c) => {
    if (!search.trim()) return true;
    const name = otherMember(c)?.username || '';
    return name.toLowerCase().includes(search.trim().toLowerCase());
  });

  const selected = conversations.find((c) => c.id === selectedId) || null;
  const selectedOther = selected ? otherMember(selected) : null;

  /** Opens the 1:1 thread with a user, creating it on first contact. */
  const openConversationWith = useCallback(
    async (otherUserId: string) => {
      if (!user || !otherUserId || otherUserId === user.id) return;
      if (openingRef.current.has(otherUserId)) return;
      openingRef.current.add(otherUserId);
      setOpening(true);

      try {
        const existing = conversations.find((c) =>
          c.conversation_members?.some((m: any) => m.user_id === otherUserId),
        );
        if (existing) {
          setSelectedId(existing.id);
          setComposing(false);
          return;
        }

        const { data: conversationId, error } =
          await messageService.getOrCreateConversation(otherUserId);

        if (error || !conversationId) {
          toast(
            'Could not start conversation',
            error?.message ||
              'Run migrations/004_direct_messaging.sql in Supabase to enable new chats.',
            'destructive',
          );
          return;
        }

        const { data: conversation } = await messageService.getConversation(conversationId);
        if (!conversation) {
          toast('Could not open conversation', 'Please try again.', 'destructive');
          return;
        }

        setConversations((prev) =>
          prev.some((c) => c.id === conversation.id) ? prev : [conversation, ...prev],
        );
        setSelectedId(conversation.id);
        setComposing(false);
      } finally {
        openingRef.current.delete(otherUserId);
        setOpening(false);
      }
    },
    [conversations, toast, user],
  );

  // Deep link from a profile: /messages?with=<user id>
  useEffect(() => {
    if (loading || !user) return;
    const withId = searchParams.get('with');
    if (!withId) {
      handledWithRef.current = null;
      return;
    }
    if (handledWithRef.current === withId) return;
    handledWithRef.current = withId;
    void openConversationWith(withId);
    setSearchParams({}, { replace: true });
  }, [loading, openConversationWith, searchParams, setSearchParams, user]);

  const handlePeopleSearch = async (q: string) => {
    if (!q.trim()) {
      setPeople([]);
      return;
    }
    setSearchingPeople(true);
    const { data } = await searchService.searchUsers(q.trim());
    setPeople((data || []).filter((p: any) => p.id !== user?.id));
    setSearchingPeople(false);
  };

  const debouncedPeopleSearch = (value: string) => {
    setPeopleQuery(value);
    if (peopleTimerRef.current) window.clearTimeout(peopleTimerRef.current);
    peopleTimerRef.current = window.setTimeout(() => {
      void handlePeopleSearch(value);
    }, 300);
  };

  const closeCompose = () => {
    setComposing(false);
    setPeopleQuery('');
    setPeople([]);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId || !draft.trim() || sending) return;
    setSending(true);
    const { data, error } = await messageService.sendMessage(selectedId, draft.trim());
    if (error) {
      toast('Failed to send', error.message, 'destructive');
    } else if (data) {
      setMessages((prev) => [...prev, data]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? { ...c, messages: [data], updated_at: data.created_at }
            : c,
        ),
      );
      setDraft('');
    }
    setSending(false);
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="skeleton-text h-4 w-32" /></div>;
  }

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col', selectedId ? 'md:mx-auto md:max-w-5xl' : 'mx-auto max-w-5xl')}>
      <div className={cn('mb-4 pt-4 shrink-0 px-4 items-center justify-between gap-3', selectedId ? 'hidden md:flex' : 'flex')}>
        <h1 className="text-2xl font-bold">Messages</h1>
        <button
          onClick={() => {
            setComposing(true);
            setSelectedId(null);
          }}
          className="flex items-center gap-2 rounded-xl bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          <PenSquare size={16} />
          <span className="hidden sm:inline">New message</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      <div className={cn('flex min-h-0 flex-1 overflow-hidden border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900', selectedId ? 'md:rounded-2xl' : 'rounded-2xl')}>
        {/* Conversation list */}
        <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col md:w-80 md:flex-none md:shrink-0 md:border-r md:border-neutral-200 md:dark:border-neutral-800', (selectedId || composing) && 'hidden md:flex')}>
          <div className="flex shrink-0 items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
            <Search size={18} className="shrink-0 text-neutral-400" />
            <input
              type="text"
              placeholder="Search messages"
              className="min-w-0 flex-1 bg-transparent text-sm focus:ring-0 placeholder:text-neutral-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-6 py-12 text-center">
                <MessageCircle size={32} className="mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No messages yet</p>
                <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                  Start a conversation with the button above.
                </p>
              </div>
            )}
            {filtered.map((c) => {
              const other = otherMember(c);
              const last = lastMessage(c);
              const unread = unreadByConversation[c.id] || 0;
              return (
                <button
                  key={c.id}
                  onClick={() => {
                    setComposing(false);
                    setSelectedId(c.id);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800',
                    selectedId === c.id && 'bg-brand-50 dark:bg-brand-900/20',
                  )}
                >
                  <UserAvatar src={other?.avatar_url} size="md" isOnline={isOnline(other)} />
                  <div className="min-w-0 flex-1">
                    <div className={cn('truncate', unread > 0 ? 'font-semibold' : 'font-medium')}>
                      {other?.display_name || other?.username || 'Conversation'}
                    </div>
                    <div className="truncate text-sm text-neutral-500 dark:text-neutral-400">
                      {last?.content || 'No messages yet'}
                    </div>
                  </div>
                  {unread > 0 ? (
                    <span
                      className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                      aria-label={`${unread} unread messages`}
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  ) : last ? (
                    <div className="shrink-0 text-xs text-neutral-400 dark:text-neutral-500">
                      {formatRelativeDate(last.created_at)}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* New message */}
        {composing ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <header className="flex shrink-0 items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <button
                onClick={closeCompose}
                className="-ml-1 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 md:hidden"
                aria-label="Back to conversations"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="min-w-0 flex-1 truncate font-semibold">New message</h2>
              <button
                onClick={closeCompose}
                className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label="Close new message"
              >
                <X size={18} />
              </button>
            </header>

            <div className="shrink-0 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <div className="flex items-center gap-2 rounded-xl bg-neutral-100 px-3 py-2 dark:bg-neutral-800/60">
                <Search size={18} className="shrink-0 text-neutral-400" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search people by username"
                  className="min-w-0 flex-1 bg-transparent text-sm focus:ring-0 placeholder:text-neutral-500"
                  value={peopleQuery}
                  onChange={(e) => debouncedPeopleSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {searchingPeople && (
                <div className="px-4 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  Searching…
                </div>
              )}
              {!searchingPeople && peopleQuery && people.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  No people found.
                </div>
              )}
              {!peopleQuery && (
                <div className="px-6 py-10 text-center">
                  <MessageCircle size={32} className="mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Find someone to start a conversation.
                  </p>
                </div>
              )}
              {people.map((p) => (
                <button
                  key={p.id}
                  onClick={() => openConversationWith(p.id)}
                  disabled={opening}
                  className="flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-neutral-50 disabled:opacity-60 dark:border-neutral-800/60 dark:hover:bg-neutral-800"
                >
                  <UserAvatar src={p.avatar_url} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{p.display_name || p.username}</div>
                    <div className="truncate text-sm text-neutral-500 dark:text-neutral-400">
                      @{p.username}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Conversation */
          <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col', !selectedId && 'hidden md:flex')}>
            {selected ? (
              <>
                <header className="flex shrink-0 items-center gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
                  <button
                    onClick={() => setSelectedId(null)}
                    className="-ml-1 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 md:hidden"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <UserAvatar src={selectedOther?.avatar_url} size="sm" isOnline={isOnline(selectedOther)} />
                  <div className="min-w-0">
                    <div className="truncate font-semibold">
                      {selectedOther?.display_name || selectedOther?.username || 'Conversation'}
                    </div>
                    <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                      @{selectedOther?.username}
                    </div>
                  </div>
                  {selectedOther && (
                    <div className="ml-auto flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => startCall(selectedOther, 'audio')}
                        className="rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                        aria-label={`Start voice call with ${selectedOther.display_name || selectedOther.username}`}
                        title="Voice call"
                      >
                        <Phone size={18} />
                      </button>
                      <button
                        onClick={() => startCall(selectedOther, 'video')}
                        className="rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                        aria-label={`Start video call with ${selectedOther.display_name || selectedOther.username}`}
                        title="Video call"
                      >
                        <Video size={18} />
                      </button>
                    </div>
                  )}
                </header>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                  {loadingMessages && <div className="skeleton-text h-4 w-24" />}
                  {!loadingMessages && messages.length === 0 && (
                    <p className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                      No messages yet. Say hello.
                    </p>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    if (m.kind === 'call') {
                      return <CallRecord key={m.id} message={m} mine={mine} />;
                    }
                    return (
                      <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                        <div
                          className={cn(
                            'max-w-[80%] rounded-2xl px-3 py-2 text-sm break-words',
                            mine
                              ? 'bg-brand-600 text-white'
                              : 'bg-neutral-200 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100',
                          )}
                        >
                          <p className="whitespace-pre-wrap">{m.content}</p>
                          <div className={cn('mt-1 text-[10px]', mine ? 'text-white/70' : 'text-neutral-500 dark:text-neutral-400')}>
                            {formatRelativeDate(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                <form onSubmit={handleSend} className="flex shrink-0 items-center gap-2 border-t border-neutral-200 px-3 py-3 dark:border-neutral-800">
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type a message..."
                    className="input-field min-w-0 flex-1 bg-neutral-100 dark:bg-neutral-800/60"
                  />
                  <button
                    type="submit"
                    disabled={!draft.trim() || sending}
                    className="rounded-xl bg-brand-600 p-2.5 text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
                    aria-label="Send message"
                  >
                    <Send size={18} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
                <MessageCircle size={40} className="mb-3 text-neutral-300 dark:text-neutral-700" />
                <p className="font-medium text-neutral-700 dark:text-neutral-300">Select a conversation</p>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  Choose a chat from the list, or start a new one.
                </p>
                <button
                  onClick={() => setComposing(true)}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
                >
                  <PenSquare size={16} />
                  New message
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
